import {
  addDays as dfAddDays, addMonths, addQuarters, addWeeks, addYears, differenceInCalendarDays,
  endOfMonth, endOfQuarter, endOfWeek, endOfYear, startOfMonth, startOfQuarter, startOfWeek, startOfYear,
} from 'date-fns'
import type { ZoomLevel } from './constants'
import { parseIsoDate, toIsoDate, type ISODate } from './format'

export interface Tick { key: string; x: number; width: number; label: string }
export interface HeaderTiers { top: Tick[]; bottom: Tick[] }

type Kind = 'day' | 'week' | 'month' | 'quarter' | 'year'
const MON = { weekStartsOn: 1 as const }
const startOf: Record<Kind, (d: Date) => Date> = { day: (d) => d, week: (d) => startOfWeek(d, MON), month: startOfMonth, quarter: startOfQuarter, year: startOfYear }
const endOf: Record<Kind, (d: Date) => Date> = { day: (d) => d, week: (d) => endOfWeek(d, MON), month: endOfMonth, quarter: endOfQuarter, year: endOfYear }
const next: Record<Kind, (d: Date) => Date> = { day: (d) => dfAddDays(d, 1), week: (d) => addWeeks(d, 1), month: (d) => addMonths(d, 1), quarter: (d) => addQuarters(d, 1), year: (d) => addYears(d, 1) }

const ticksFor = (kind: Kind, rangeStart: ISODate, fromDay: number, toDay: number, pxPerDay: number, label: (d: Date) => string): Tick[] => {
  const base = parseIsoDate(rangeStart)
  const limit = dfAddDays(base, toDay)
  const out: Tick[] = []
  let cur = startOf[kind](dfAddDays(base, fromDay))
  while (cur <= limit) {
    const s = differenceInCalendarDays(cur, base)
    const e = differenceInCalendarDays(endOf[kind](cur), base)
    out.push({ key: `${kind}-${toIsoDate(cur)}`, x: s * pxPerDay, width: (e - s + 1) * pxPerDay, label: label(cur) })
    cur = next[kind](cur)
  }
  return out
}

export const headerTiers = (zoom: ZoomLevel, rangeStart: ISODate, fromDay: number, toDay: number, pxPerDay: number, locale = 'en-US'): HeaderTiers => {
  const monthYear = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' })
  const monthShort = new Intl.DateTimeFormat(locale, { month: 'short' })
  const dayMonth = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'numeric' })
  const weekdayNarrow = new Intl.DateTimeFormat(locale, { weekday: 'narrow' })
  const year = (d: Date) => String(d.getFullYear())
  const quarter = (d: Date) => `Q${Math.floor(d.getMonth() / 3) + 1}`
  const T = (kind: Kind, label: (d: Date) => string) => ticksFor(kind, rangeStart, fromDay, toDay, pxPerDay, label)
  switch (zoom) {
    case 'day': return { top: T('month', (d) => monthYear.format(d)), bottom: T('day', (d) => `${d.getDate()} ${weekdayNarrow.format(d)}`) }
    case 'week': return { top: T('month', (d) => monthYear.format(d)), bottom: T('week', (d) => dayMonth.format(d)) }
    case 'month': return { top: T('year', year), bottom: T('month', (d) => monthShort.format(d)) }
    case 'quarter': return { top: T('year', year), bottom: T('quarter', quarter) }
    case 'project': return { top: T('year', year), bottom: pxPerDay >= 1.2 ? T('quarter', quarter) : [] }
  }
}
